import { cn } from '@/lib/cn';

interface Props {
  value: number;
  label?: string;
  showValue?: boolean;
  size?: 'sm' | 'md';
  tone?: 'tech' | 'ai' | 'agent' | 'ok';
  className?: string;
}

const TONES = {
  tech: 'from-tech to-tech',
  ai: 'from-ai to-ai',
  agent: 'from-agent to-agent',
  ok: 'from-ok to-ok',
} as const;

export function ProgressBar({
  value,
  label,
  showValue = true,
  size = 'md',
  tone = 'tech',
  className,
}: Props) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={className}>
      {(label || showValue) && (
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          {label ? <span className="text-xs font-medium text-muted">{label}</span> : <span />}
          {showValue ? (
            <span className="text-xs font-semibold tabular-nums text-fg">{clamped} %</span>
          ) : null}
        </div>
      )}
      <div
        className={cn('w-full overflow-hidden rounded-full bg-surface-2', size === 'sm' ? 'h-1.5' : 'h-2.5')}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progression'}
      >
        <div
          className={cn('h-full rounded-full bg-gradient-to-r transition-[width] duration-500', TONES[tone])}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

export function ProgressRing({
  value,
  size = 96,
  label,
}: {
  value: number;
  size?: number;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          className="stroke-surface-2"
          fill="none"
        />
        <defs>
          <linearGradient id="kha-ring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgb(var(--kha-tech))" />
            <stop offset="100%" stopColor="rgb(var(--kha-ai))" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          stroke="url(#kha-ring)"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          fill="none"
          className="transition-[stroke-dashoffset] duration-700"
        />
      </svg>
      <div className="absolute text-center">
        <div className="text-xl font-bold tabular-nums text-fg">{clamped} %</div>
        {label ? <div className="text-2xs text-faint">{label}</div> : null}
      </div>
      <span className="sr-only">{`${label ?? 'Progression'} : ${clamped} %`}</span>
    </div>
  );
}
